import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BULK_TIERS, unitPriceFor, tierFor } from "@/data/products";
import { useCatalog } from "@/context/CatalogContext";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/lib/track";
import { ArrowLeft, Minus, Plus, FlaskConical, Snowflake, ShieldCheck, Hash, Clock, Dna, BellRing, Tag, FileCheck, ChevronDown, Microscope, Search } from "lucide-react";
import RestockDialog from "@/components/RestockDialog";
import FreeShipBanner from "@/components/FreeShipBanner";
import { useStock } from "@/context/StockContext";
import Seo, { SITE_URL } from "@/components/Seo";
import { useResearchIndex } from "@/hooks/useResearchIndex";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isSoldOut } = useStock();
  const { products, getProduct, loading } = useCatalog();
  const { byProduct } = useResearchIndex();
  const [qty, setQty] = useState(1);
  const [restock, setRestock] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batches, setBatches] = useState(null);
  const [lotQ, setLotQ] = useState("");
  const [lotRes, setLotRes] = useState(null);
  const [lotBusy, setLotBusy] = useState(false);
  const p = getProduct(id);

  const verifyLot = async () => {
    const key = lotQ.trim();
    if (!key || lotBusy) return;
    setLotBusy(true);
    try {
      const axios = (await import("axios")).default;
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/batches/verify`, { params: { lot: key } });
      setLotRes(data);
    } catch { setLotRes({ found: false, lot: key }); }
    setLotBusy(false);
  };

  const openBatches = async () => {
    setBatchOpen(true);
    if (batches === null) {
      try {
        const axios = (await import("axios")).default;
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/batches/public`, { params: { compound: p.name } });
        setBatches(data.batches || []);
      } catch { setBatches([]); }
    }
  };

  useEffect(() => {
    if (p?.id) trackEvent("product_view", { product_id: p.id, product_name: p.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.id]);

  if (loading) {
    return <main className="pt-40 pb-24 text-center" data-testid="product-loading"><p className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B]">Loading compound…</p></main>;
  }

  if (!p) {
    return (
      <main className="pt-40 pb-24 text-center" data-testid="product-not-found">
        <p className="font-serif text-4xl font-light mb-6">Compound not found.</p>
        <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4AF37]">← Back to catalog</Link>
      </main>
    );
  }

  const unit = unitPriceFor(p.price, qty);
  const tier = tierFor(qty);
  const soldOut = isSoldOut(p);
  const related = products.filter((x) => x.id !== p.id).slice(0, 3);
  const research = byProduct[p.id];

  const specs = [
    { icon: Tag, label: "SKU", value: p.sku },
    { icon: ShieldCheck, label: "Purity", value: p.purity },
    { icon: FlaskConical, label: "Form", value: p.form },
    { icon: Snowflake, label: "Quantity", value: p.size },
    { icon: Hash, label: "CAS No.", value: p.cas },
    { icon: Clock, label: "Half-life", value: p.halfLife },
    { icon: Dna, label: "Sequence", value: p.sequence },
  ];

  const productJsonLd = {
    "@context": "https://schema.org", "@type": "Product",
    "name": p.name, "sku": p.sku, "category": p.category, "description": p.blurb,
    "brand": { "@type": "Brand", "name": "Molecularix Biolabs" },
    "offers": {
      "@type": "Offer", "priceCurrency": "USD", "price": p.price,
      "availability": soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      "url": `${SITE_URL}/product/${p.id}`,
    },
  };

  return (
    <main className="pt-28 pb-24" data-testid="product-detail-page">
      <Seo title={`${p.name} — ${p.size}`} path={`/product/${p.id}`} type="product"
        description={`${p.name} (${p.sku}), ${p.size}. ${p.category}. Reference-grade research material — for research use only.`}
        jsonLd={productJsonLd} />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <button onClick={() => navigate(-1)} data-testid="detail-back-btn"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors mb-12">
          <ArrowLeft size={15} /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative border border-zinc-800 bg-black aspect-square overflow-hidden">
            <img src={p.image} alt={p.name} className="w-full h-full object-cover" data-testid="detail-image" />
            <span className="absolute top-5 left-5 font-mono text-base font-semibold uppercase tracking-[0.12em] text-[#D4AF37] bg-black/65 backdrop-blur px-3 py-1.5">{p.size}</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">{p.category || "Research Compound"}</p>
            <h1 className="font-serif text-4xl md:text-6xl font-light leading-none mb-3" data-testid="detail-name">{p.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <p className="font-mono text-[11px] tracking-[0.1em] text-[#71717A]" data-testid="detail-sku">SKU: {p.sku}</p>
              {research?.identityVerified && (
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-400/30 bg-emerald-400/[0.06] px-2.5 py-1" data-testid="detail-verified-badge">
                  <ShieldCheck size={12} /> Verified Identity
                </span>
              )}
              <button onClick={openBatches} data-testid="detail-batch-btn"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] border border-[#D4AF37]/40 px-2.5 py-1 hover:bg-[#D4AF37] hover:text-black transition-colors">
                <FlaskConical size={12} /> Batch / Lab Testing
              </button>
            </div>

            {batchOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setBatchOpen(false)} data-testid="batch-modal"
                role="dialog" aria-modal="true" aria-label="Lab COA and batch testing"
                onKeyDown={(e) => e.key === "Escape" && setBatchOpen(false)} tabIndex={-1} ref={(el) => el && el.focus()}>
                <div className="bg-[#0C0C0C] border border-[#1E1E24] max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Batch Records — {p.name}</p>
                    <button onClick={() => setBatchOpen(false)} data-testid="batch-modal-close" className="font-mono text-xs text-[#71717A] hover:text-[#F4F4F5]">✕ Close</button>
                  </div>
                  {batches === null ? (
                    <p className="font-mono text-xs text-[#71717A] py-6 text-center">Loading batch records…</p>
                  ) : batches.length ? (
                    batches.map((b) => (
                      <div key={b.lot} className="border border-[#1E1E24] p-4 mb-3" data-testid={`batch-record-${b.lot}`}>
                        <p className="font-mono text-sm text-[#F4F4F5] mb-1">Lot {b.lot}{b.date ? ` · ${b.date}` : ""}</p>
                        <p className="font-mono text-[11px] text-[#A1A1AA]">Identity: {b.identity || "See batch record"}</p>
                        <p className="font-mono text-[11px] text-[#A1A1AA]">Purity: {b.purity || "See batch record"}</p>
                        {b.coa_url && <a href={b.coa_url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-[#D4AF37] hover:underline">View lab document →</a>}
                      </div>
                    ))
                  ) : (
                    <div className="py-4">
                      <p className="text-[#A1A1AA] text-sm mb-4">No published batch records for this compound yet. You can verify any lot number from your vial, or request batch documentation:</p>
                      <div className="flex flex-wrap gap-3">
                        <Link to="/verify" className="border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid="batch-verify-link">Verify a Lot</Link>
                        <Link to="/documentation" className="border border-zinc-700 text-[#A1A1AA] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="batch-request-link">Request Documentation</Link>
                      </div>
                    </div>
                  )}
                  <p className="font-mono text-[9px] text-[#52525B] mt-4">Only real, recorded lab data is shown. For Research &amp; Laboratory Use Only.</p>
                  <div className="border-t border-[#1E1E24] mt-4 pt-4" data-testid="coa-verify-lookup">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Verify a lot number from your vial</p>
                    <div className="flex gap-2">
                      <input value={lotQ} onChange={(e) => setLotQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verifyLot()}
                        placeholder="e.g. MLX-2406-A1" data-testid="coa-verify-input"
                        className="flex-1 min-w-0 bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37] uppercase" />
                      <button onClick={verifyLot} disabled={lotBusy} data-testid="coa-verify-btn"
                        className="flex items-center gap-1.5 bg-[#D4AF37] text-black px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#C5A028] transition-colors disabled:opacity-50">
                        <Search size={12} /> {lotBusy ? "…" : "Verify"}
                      </button>
                    </div>
                    {lotRes && (lotRes.found ? (
                      <div className="mt-3 border border-emerald-400/30 bg-emerald-400/[0.05] p-3" data-testid="coa-verify-found">
                        <p className="font-mono text-xs text-emerald-400 mb-1">✓ Lot {lotRes.lot} verified on record</p>
                        <p className="font-mono text-[11px] text-[#A1A1AA]">Compound: {lotRes.compound || "—"} · Purity: {lotRes.purity || "See batch record"}</p>
                        {lotRes.coa_url && <a href={lotRes.coa_url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-[#D4AF37] hover:underline">View lab document →</a>}
                      </div>
                    ) : (
                      <p className="mt-3 font-mono text-[11px] text-red-400" data-testid="coa-verify-notfound">Lot "{lotRes.lot}" is not on file. Double-check the number or request documentation.</p>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed mb-8">{p.blurb}</p>

            <div className="flex items-baseline gap-3 mb-8">
              {soldOut ? (
                <span className="font-mono text-sm uppercase tracking-[0.2em] text-red-400 border border-red-400/40 px-3 py-1.5" data-testid="detail-sold-out">Sold Out</span>
              ) : tier ? (
                <>
                  <span className="font-serif text-5xl" data-testid="detail-unit-price">${unit}</span>
                  <span className="font-serif text-2xl text-[#52525B] line-through">${p.price}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wide text-emerald-400 border border-emerald-400/40 px-2 py-1">{tier.label}</span>
                </>
              ) : (
                <span className="font-serif text-5xl" data-testid="detail-unit-price">${p.price}<span className="font-mono text-sm text-[#52525B] ml-2">/vial</span></span>
              )}
            </div>

            <div className="grid grid-cols-3 border border-[#1E1E24] divide-x divide-[#1E1E24] bg-[#0C0C0C] mb-6" data-testid="trust-badges">
              {[{ Icon: ShieldCheck, t: /99/.test(p.purity || "") ? ">99% Purity" : "Purity", s: "Verified" },
                { Icon: Microscope, t: "Third-Party", s: "Tested" },
                { Icon: FlaskConical, t: "Research", s: "Use Only" }].map((b) => (
                <div key={b.t} className="flex flex-col items-center gap-1.5 py-3.5 px-1 text-center">
                  <b.Icon size={17} color="#D4AF37" strokeWidth={1.5} />
                  <p className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.12em] text-[#F4F4F5] leading-tight">{b.t}<br /><span className="text-[#71717A]">{b.s}</span></p>
                </div>
              ))}
            </div>

            {soldOut ? (
              <button onClick={() => setRestock(true)} data-testid="detail-notify-restock"
                className="w-full flex items-center justify-center gap-3 border border-[#D4AF37]/50 text-[#D4AF37] py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors mb-6">
                <BellRing size={16} /> Notify me when back in stock
              </button>
            ) : (
              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center border border-zinc-700">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-11 flex items-center justify-center hover:text-[#D4AF37]" data-testid="detail-qty-dec"><Minus size={15} /></button>
                  <span className="w-12 text-center font-mono" data-testid="detail-qty">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="w-11 h-11 flex items-center justify-center hover:text-[#D4AF37]" data-testid="detail-qty-inc"><Plus size={15} /></button>
                </div>
                <button onClick={() => addItem(p, qty)} data-testid="detail-add-to-cart"
                  className="flex-1 bg-[#D4AF37] text-black py-4 px-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.2em] whitespace-nowrap hover:bg-[#C5A028] transition-colors">
                  Add {qty} to Order — ${unit * qty}
                </button>
              </div>
            )}

            <FreeShipBanner variant="inline" className="mb-6" />

            <button onClick={openBatches} data-testid="view-coa-btn"
              className="w-full flex items-center justify-center gap-2.5 border border-[#D4AF37] bg-[#D4AF37]/[0.07] text-[#D4AF37] py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors mb-4">
              <FileCheck size={16} /> View Lab COA &amp; Batch Testing
            </button>

            {research?.slug && (
              <Link to={`/library/${research.slug}`} data-testid="detail-research-link"
                className="w-full flex items-center justify-center gap-2 border border-zinc-700 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors mb-4">
                <FlaskConical size={15} /> Read Research Overview
              </Link>
            )}

            <div className="mb-8 space-y-3">
              <details open className="group border border-zinc-800 bg-[#0C0C0C]" data-testid="spec-accordion-molecular">
                <summary className="flex items-center justify-between cursor-pointer list-none p-4 select-none">
                  <span className="flex items-center gap-3">
                    <Dna size={16} color="#D4AF37" strokeWidth={1.5} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F4F5]">Molecular Specifications</span>
                  </span>
                  <ChevronDown size={15} className="text-[#52525B] transition-transform group-open:rotate-180" />
                </summary>
                <div className="border-t border-zinc-800 divide-y divide-zinc-800">
                  {specs.filter((s) => s.value).map((s) => (
                    <div key={s.label} className="flex items-center gap-4 p-4">
                      <s.icon size={18} color="#D4AF37" strokeWidth={1.4} className="shrink-0" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8B8B94] w-24 shrink-0">{s.label}</span>
                      <span className="text-[#F5F5F0] text-sm flex-1 break-words font-mono">{s.value}</span>
                    </div>
                  ))}
                </div>
              </details>

              <details className="group border border-zinc-800 bg-[#0C0C0C]" data-testid="spec-accordion-storage">
                <summary className="flex items-center justify-between cursor-pointer list-none p-4 select-none">
                  <span className="flex items-center gap-3">
                    <Snowflake size={16} color="#D4AF37" strokeWidth={1.5} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F4F5]">Storage &amp; Handling</span>
                  </span>
                  <ChevronDown size={15} className="text-[#52525B] transition-transform group-open:rotate-180" />
                </summary>
                <div className="border-t border-zinc-800 p-4 space-y-3">
                  {p.storage && <p className="text-[#A1A1AA] text-sm leading-relaxed">{p.storage}</p>}
                  <div className="divide-y divide-zinc-900">
                    {[["Long-term storage", "Lyophilized vials are stored at −20°C per standard laboratory practice."],
                      ["Post-reconstitution", "Reconstituted material is typically held at 2–8°C per laboratory SOP."],
                      ["Handling", "Add solvent slowly down the vial wall to avoid foaming; swirl gently — never shake."],
                      ["Light protection", "Keep vials protected from direct light; store in original packaging."]].map(([k, v]) => (
                      <div key={k} className="flex gap-4 py-2.5">
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B] w-28 shrink-0 pt-0.5">{k}</span>
                        <span className="text-[#A1A1AA] text-[13px] leading-relaxed flex-1">{v}</span>
                      </div>
                    ))}
                  </div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-[#52525B]">Handling guidance is provided for laboratory personnel only.</p>
                </div>
              </details>
            </div>

            <div className="space-y-6">
              <div className="border border-zinc-800 bg-[#0C0C0C] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] mb-3">Volume Pricing</p>
                <ul className="space-y-1.5">
                  {BULK_TIERS.slice().reverse().map((t) => (
                    <li key={t.min} className="flex justify-between font-mono text-xs">
                      <span className="text-[#71717A]">{t.label}</span>
                      <span className="text-emerald-400">${Math.round(p.price * (1 - t.off))}/vial</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <p className="mt-8 text-[#52525B] text-[11px] font-mono uppercase tracking-wide leading-relaxed">
              For laboratory research use only. Not for human consumption.
            </p>
          </motion.div>
        </div>

        <div className="mt-28">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-10">Related Compounds</p>
          <div className="grid sm:grid-cols-3 gap-px">
            {related.map((r) => (
              <Link key={r.id} to={`/product/${r.id}`} className="group border border-zinc-800 hover:border-[#D4AF37]/50 bg-[#0C0C0C] transition-colors" data-testid={`related-${r.id}`}>
                <div className="aspect-square overflow-hidden bg-black">
                  <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5 flex items-center justify-between">
                  <span className="font-serif text-xl font-light">{r.name}</span>
                  <span className="font-serif text-xl">${r.price}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <RestockDialog product={p} open={restock} onOpenChange={setRestock} />
    </main>
  );
}
